import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import InputAdornment from "@mui/material/InputAdornment";
import { Input } from "../../../../../components/input";
import { CustomCheckbox } from "../../../../../components/custom-checkbox";
import { RequiredLabel } from "../../../../../components/form";
import { cn } from "../../../../../lib/utils";
import { MerchantAccountFormData } from "../../types/merchantRegister.types";
import { TERMS_URL } from "../../../../../config/runtime";
import MerchantStepButtons from "../MerchantStepButtons";
import { useNavigate } from "react-router-dom";
import { useRegisterMerchantAccount } from "../../hooks/useMerchantRegistrationActions";
import { createMerchantAccountSchema } from "../../schemas/merchantRegister.schemas";

const PHONE_NUMBER_DIGIT_COUNT = 10;
const PHONE_NUMBER_PLACEHOLDER = "(___) ___-____";

const formatPhoneNumber = (value: string) => {
  const digits = value.slice(0, PHONE_NUMBER_DIGIT_COUNT);

  if (digits.length <= 3) {
    return digits ? `(${digits}` : "";
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const Step1BasicInfo: React.FC = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const createAccount = useRegisterMerchantAccount();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MerchantAccountFormData>({
    resolver: zodResolver(createMerchantAccountSchema(t)),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
  });

  const onCreate = () => {
    void handleSubmit(async (values) => {
      const result = await createAccount.execute(values);
      if (result) {
        navigate("/merchant/register/verify-email");
      }
    })();
  };

  return (
    <div className="w-full flex justify-between flex-col h-full">
      <div>
        <div className="flex h-[78px] flex-col justify-center font-['Nunito',sans-serif] text-center text-[24px] font-bold text-black dark:text-white">
          <p className="leading-[28px]">{t("merchantRegister.step1.title")}</p>
        </div>

        <div className="flex flex-col gap-[25px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 md:gap-x-[45px]">
            <FormField
              label={t("merchantRegister.step1.firstName")}
              inputId="firstName"
              required
              error={errors.firstName?.message}
            >
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="firstName"
                    type="text"
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 dark:text-white",
                      errors.firstName && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>

            <FormField
              label={t("merchantRegister.step1.lastName")}
              inputId="lastName"
              required
              error={errors.lastName?.message}
            >
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="lastName"
                    type="text"
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 dark:text-white",
                      errors.lastName && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 md:gap-x-[45px]">
            <FormField
              label={t("merchantRegister.step1.phoneNumber")}
              inputId="phoneNumber"
              required
              error={errors.phoneNumber?.message}
            >
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={PHONE_NUMBER_PLACEHOLDER}
                    maxLength={PHONE_NUMBER_PLACEHOLDER.length}
                    value={formatPhoneNumber(field.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <span className="border-r border-[#DCDCDC] pr-3 font-semibold text-[#75757D] dark:border-gray-600 dark:text-gray-400">
                          +1
                        </span>
                      </InputAdornment>
                    }
                    onChange={(event) => {
                      field.onChange(
                        event.target.value.replace(/\D/g, "").slice(0, PHONE_NUMBER_DIGIT_COUNT),
                      );
                    }}
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 dark:text-white",
                      errors.phoneNumber && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>

            <FormField
              label={t("merchantRegister.step1.email")}
              inputId="email"
              required
              error={errors.email?.message}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 dark:text-white",
                      errors.email && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 md:gap-x-[45px]">
            <FormField
              label={t("merchantRegister.step1.password")}
              inputId="password"
              required
              error={errors.password?.message}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    endIcon={showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    endIconAriaLabel={t(
                      showPassword
                        ? "merchantRegister.step1.hidePassword"
                        : "merchantRegister.step1.showPassword",
                    )}
                    onEndIconClick={() => setShowPassword((value) => !value)}
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 pr-12 dark:text-white",
                      errors.password && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>

            <FormField
              label={t("merchantRegister.step1.confirmPassword")}
              inputId="confirmPassword"
              required
              error={errors.confirmPassword?.message}
            >
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    endIcon={showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    endIconAriaLabel={t(
                      showConfirmPassword
                        ? "merchantRegister.step1.hidePassword"
                        : "merchantRegister.step1.showPassword",
                    )}
                    onEndIconClick={() => setShowConfirmPassword((value) => !value)}
                    className={cn(
                      "h-12 md:h-[60px] bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg px-4 md:px-5 pr-12 dark:text-white",
                      errors.confirmPassword && "border-[#EC5454]",
                    )}
                  />
                )}
              />
            </FormField>
          </div>

          <Controller
            name="agreeToTerms"
            control={control}
            render={({ field }) => (
              <div>
                <div className="flex items-center gap-3">
                  <CustomCheckbox
                    id="agreeToTerms"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <label
                    htmlFor="agreeToTerms"
                    className="text-[15px] text-[#75757D] dark:text-gray-400 font-medium font-nunito cursor-pointer"
                  >
                    {t("merchantRegister.step1.agreeToTerms")}{" "}
                    <a
                      href={TERMS_URL}
                      className="text-[#004232] dark:text-[#00C49A] font-bold underline"
                    >
                      {t("merchantRegister.step1.termsOfService")}
                    </a>
                  </label>
                </div>
                {errors.agreeToTerms && (
                  <p className="text-[12px] text-[#EC5454] font-semibold font-nunito mt-[7px]">
                    {errors.agreeToTerms.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>
      </div>
      <div className="flex-1 flex items-end justify-end">
        <MerchantStepButtons
          previousBtn={{
            label: t("merchantRegister.buttons.back"),
            onClick: () => navigate("/login"),
          }}
          nextBtn={{
            label: t("merchantRegister.buttons.next"),
            loading: createAccount.isLoading,
            onClick: onCreate,
          }}
        />
      </div>
    </div>
  );
};

interface FormFieldProps {
  label: string;
  inputId: string;
  required?: boolean;
  error?: string | FieldError;
  hint?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  inputId,
  required,
  error,
  hint,
  children,
}) => (
  <div className="flex flex-col">
    <RequiredLabel
      htmlFor={inputId}
      className="text-base font-semibold text-[#75757D] dark:text-gray-400 font-nunito mb-[15px]"
      required={required}
    >
      {label}
    </RequiredLabel>
    {children}
    {error && (
      <p className="text-[12px] text-[#EC5454] font-semibold font-nunito leading-[1.36] mt-[7px]">
        {typeof error === "string" ? error : error.message}
      </p>
    )}
    {hint && !error && (
      <p className="text-[12px] text-[#75757D] dark:text-gray-400 font-semibold font-nunito leading-[1.36] mt-[7px]">
        {hint}
      </p>
    )}
  </div>
);

export default Step1BasicInfo;
